import {useEffect, useState} from "react";
import {Card} from "primereact/card";
import {Button} from "primereact/button";
import {ProgressBar} from "primereact/progressbar";
import {Message} from "primereact/message";
import {API} from "./App";
import {getAuthHeader} from "./AuthRoute";

export default function ExcelGenerationList() {
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [downloadingId, setDownloadingId] = useState(null);

    const fetchTasks = async () => {
        try {
            setError(null);
            const res = await fetch(API() + `/projects/generation-status`, {
                headers: {Authorization: getAuthHeader()},
            });
            if (!res.ok) throw new Error(`Server returned ${res.status}`);
            const data = await res.json();
            setTasks(data);
        } catch (err) {
            console.error("Failed to fetch tasks:", err);
            setError("Failed to load generation tasks. Please retry.");
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async (resultId, projectName) => {
        setDownloadingId(resultId);
        try {
            const res = await fetch(API() + `/projects/generation-result/${resultId}`, {
                headers: {Authorization: getAuthHeader()},
            });
            if (res.status !== 200) throw new Error(res.status);
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `${projectName}_generated.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            alert("Download failed: " + err.message);
        } finally {
            setDownloadingId(null);
        }
    };

    useEffect(() => {
        fetchTasks();
        const interval = setInterval(fetchTasks, 10000);
        return () => clearInterval(interval);
    }, []);

    if (loading) return <p>Loading…</p>;

    if (error)
        return (
            <div className="p-d-flex p-flex-column p-ai-center">
                <Message severity="error" text={error} className="mb-3"/>
                <Button label="Retry" icon="pi pi-refresh" onClick={fetchTasks}/>
            </div>
        );

    if (!tasks.length)
        return <Message severity="info" text="No active generations."/>;

    return (
        <div className="p-grid p-dir-col" style={{gap: "1rem"}}>
            {tasks.map((t) => {
                const startedAt = new Date(t.started_at);
                const now = new Date();
                const hoursElapsed = (now - startedAt) / (1000 * 60 * 60);
                const isStuck = t.status !== "done" && hoursElapsed > 5;

                return (
                    <Card
                        key={t.result_id}
                        title={t.project_name}
                        subTitle={startedAt.toLocaleString()}
                        className="p-shadow-2"
                    >
                        <div className="p-d-flex p-ai-center p-jc-between">
                            <div>
                                <p>
                                    Requested by: <b>{t.user_id}</b>
                                </p>
                                <p>
                                    Made for platform : <b>{t.platform}</b>
                                </p>
                                <p>
                                    Status:{" "}
                                    <b className={t.status === "done" ? "text-green-600" : "text-orange-500"}>
                                        {t.status}
                                    </b>
                                </p>

                                {t.eta && (
                                    <Message severity="info" text={`ETA: ${t.eta}`} className="mb-2"/>
                                )}

                                {isStuck && (
                                    <Message
                                        severity="warn"
                                        text="This process started a long time ago and was probably killed."
                                        className="mb-2"
                                    />
                                )}

                                {t.status !== "done" && !t.error && (
                                    <ProgressBar
                                        mode={isStuck ? "determinate" : "indeterminate"}
                                        value={isStuck ? 100 : undefined}
                                        style={{height: "6px", width: "150px"}}
                                    />
                                )}

                            </div>

                            {t.status === "done" ? (
                                <Button
                                    label={
                                        downloadingId === t.result_id ? "Downloading..." : "Download"
                                    }
                                    icon={
                                        downloadingId === t.result_id
                                            ? "pi pi-spin pi-cog"
                                            : "pi pi-download"
                                    }
                                    disabled={!!downloadingId}
                                    onClick={() => handleDownload(t.result_id, t.project_name)}
                                />
                            ) : (
                                <Button label="Processing..." icon="pi pi-spin pi-cog" disabled/>
                            )}
                        </div>
                    </Card>
                );
            })}
        </div>
    );
}
